import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';

import { ReviewsFilterOptionsModel } from 'models/common';

import { classNames } from 'uitk-react-utils';
import { UitkCheckbox, UitkRadio, UitkRadioGroup } from 'uitk-react-switch';
import { UitkPrimaryButton, UitkTertiaryButton } from 'uitk-react-button';

export interface ReviewsFilterOptionsProps {
  showButtons?: boolean;
  checkboxOptions: ReviewsFilterOptionsModel[];
  radioOptions?: ReviewsFilterOptionsModel[];
  className?: string;
  applyOptionsCallback?: (values) => void;
  label?: string;
  radioGroupName?: string;
  reviewLanguageFilters?: string;
  toggleSheet?: () => void;
}

export interface ReviewsFilterOptionsState {
  options: object;
  radioSelected: string;
}

export class ReviewsFilterOptions extends React.Component<ReviewsFilterOptionsProps, ReviewsFilterOptionsState> {
  public constructor(props) {
    super(props);
    const { checkboxOptions, reviewLanguageFilters } = this.props;
    const options = checkboxOptions ? this.createOptionsModel(checkboxOptions) : [];
    const radioSelected = typeof reviewLanguageFilters === 'string' ? reviewLanguageFilters : '';
    this.state = {
      options,
      radioSelected
    };

    this.clearSelectedFilters = this.clearSelectedFilters.bind(this);
    this.applySelectedFilters = this.applySelectedFilters.bind(this);
    this.applyRadioFilters = this.applyRadioFilters.bind(this);
    this.handleOptionSelect = this.handleOptionSelect.bind(this);
    this.handleRadioChange = this.handleRadioChange.bind(this);
  }

  private createOptionsModel(options) {
    const optionsModel = {};
    options.map(option => (optionsModel[option.value] = option.selected));

    return optionsModel;
  }

  private clearSelectedFilters() {
    const { options } = this.state;
    const optionsModel = {};
    Object.keys(options).forEach(option => (optionsModel[option] = false));

    this.setState({
      options: optionsModel
    });
  }

  private applySelectedFilters() {
    const { options } = this.state;
    const { applyOptionsCallback } = this.props;
    const selectedValues = [];

    for (const key in options) {
      if (options.hasOwnProperty(key)) {
        if (options[key] === true) {
          selectedValues.push(key);
        }
      }
    }

    applyOptionsCallback(selectedValues);
  }

  private handleOptionSelect(value) {
    const { showButtons } = this.props;
    const currentSelectionState = this.state.options[value];
    const selectCallback = showButtons ? null : this.applySelectedFilters;

    this.setState(
      {
        options: {
          ...this.state.options,
          [value]: !currentSelectionState
        }
      },
      selectCallback
    );
  }

  private applyRadioFilters(): void {
    const { radioSelected } = this.state;
    const { applyOptionsCallback } = this.props;

    if (applyOptionsCallback && typeof applyOptionsCallback === 'function') {
      applyOptionsCallback(radioSelected);
    }
  }

  private handleRadioChange(e: React.FormEvent<HTMLInputElement>): void {
    const { showButtons } = this.props;
    const selectCallback = showButtons ? null : this.applyRadioFilters;
    this.setState(
      {
        radioSelected: e.currentTarget.value
      },
      selectCallback
    );
  }

  public render() {
    const { label, checkboxOptions, showButtons, className, radioGroupName, radioOptions } = this.props;
    const { options } = this.state;
    const filterOptionsClasses = classNames('all-t-padding-four s-x-padding-four', className);
    const ClearButtonTracked = ClickTracker(UitkTertiaryButton);
    const ApplyButtonTracked = ClickTracker(UitkPrimaryButton);

    return (
      <div className={filterOptionsClasses}>
        {label && <div className="uitk-type-bold uitk-type-heading-500 all-b-padding-two">{label}</div>}
        <FilterOptions
          checkboxOptions={checkboxOptions}
          checkboxOptionsState={options}
          radioOptions={radioOptions}
          radioGroupName={radioGroupName}
          radioSelected={this.state.radioSelected}
          handleRadioChange={this.handleRadioChange}
          handleOptionSelect={this.handleOptionSelect}
        />
        {showButtons && (
          <div className="uitk-grid all-cell-1-1 all-b-padding-four">
            <div className="uitk-cell uitk-type-right all-t-padding-two">
              {checkboxOptions && (
                <ClearButtonTracked
                  className="uitk-type-right"
                  text="Clear"
                  onClick={this.clearSelectedFilters}
                  rfrr="Reviews.FilterClear"
                  linkName="Reviews Filter Clear"
                />
              )}
              <ApplyButtonTracked
                className="uitk-type-right"
                text="Apply"
                onClick={checkboxOptions ? this.applySelectedFilters : this.applyRadioFilters}
                rfrr="ReviewsFilterApply"
                linkName="Review Filters Apply"
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}

export interface FilterOptionsProps {
  checkboxOptions?: ReviewsFilterOptionsModel[];
  checkboxOptionsState?: object;
  handleOptionSelect?: (value) => void;
  radioOptions?: ReviewsFilterOptionsModel[];
  radioGroupName?: string;
  radioSelected?: string;
  handleRadioChange?: (e) => void;
}

export const FilterOptions: React.FunctionComponent<FilterOptionsProps> = props => {
  const {
    checkboxOptions,
    checkboxOptionsState,
    handleOptionSelect,
    radioOptions,
    radioGroupName,
    radioSelected,
    handleRadioChange
  } = props;
  const RadioButtonTracked = ClickTracker(UitkRadio);

  if (checkboxOptions) {
    const OptionTracked = ClickTracker(UitkCheckbox);

    return (
      <>
        {checkboxOptions.map((option, index) => {
          const optionListener = () => handleOptionSelect(option.value);

          return (
            <div key={index} className="uitk-type-300 pwa-theme--grey-700">
              <OptionTracked
                id={option.label}
                label={option.label}
                checked={checkboxOptionsState[option.value]}
                onChange={optionListener}
                rfrr={`Reviews.Filter${option.trackingId}`}
                linkName="Reviews Filter Rating"
              />
            </div>
          );
        })}
      </>
    );
  }

  return (
    <>
      <UitkRadioGroup group={radioGroupName} onChange={handleRadioChange} selected={radioSelected}>
        {radioOptions.map((option, index) => (
          <RadioButtonTracked
            id={`radio-${radioGroupName}-${option.label}`}
            label={option.label}
            value={option.value}
            key={`${radioGroupName}-${index}-${option.value}`}
            rfrr={`Reviews.Filter${option.trackingId}`}
            name={option.label}
            linkName={`${radioGroupName}.${option.label}`}
            events="click"
            aria-labelledby={`${radioGroupName}-legend ${radioGroupName}-${option.label}-label`}
            data-stid={`radio-${radioGroupName}-${option.label}`}
            group={option.filterName}
          />
        ))}
      </UitkRadioGroup>
    </>
  );
};
FilterOptions.displayName = 'FilterOptions';
